import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/** @typedef {{ key: string; product_id: number; variant_id: number; quantity: number }} CartLineItem */
/** @typedef {{ items: CartLineItem[] }} CartState */

const DISMISSED_STORAGE_KEY = 'cart_warranty_dismissed';

/** Module-level lock so multiple cart-warranty instances (drawer + cart page) never auto-add concurrently. */
let autoAddInFlight = false;

/**
 * Web component that manages warranty selection in the cart.
 * Allows adding/swapping/removing a single warranty variant,
 * auto-adds the default (cheapest) variant when missing (unless the
 * customer dismissed it for this session), and auto-removes orphaned
 * warranties when no other products remain.
 */
class CartWarranty extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initWarranty();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Re-runs the warranty init flow whenever the cart is updated elsewhere
   * (e.g. customer adds a product from a PDP, or removes the last
   * non-warranty item leaving an orphan warranty).
   */
  #handleCartUpdate = () => {
    this.#initWarranty();
  };

  /** @returns {number} */
  get #defaultVariantId() {
    return Number(this.dataset.defaultVariantId) || 0;
  }

  /**
   * Init flow run on mount and on every cart update:
   *   1. consolidate — guarantee at most ONE warranty line exists.
   *   2. cleanOrphan — remove warranty if no real products remain.
   *   3. autoAdd     — add the default warranty if missing and not dismissed.
   */
  async #initWarranty() {
    await this.#consolidateWarranties();
    await this.#cleanOrphanedWarranty();
    await this.#autoAddDefaultWarranty();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

  /** @returns {number} */
  get #warrantyProductId() {
    return Number(this.dataset.warrantyProductId);
  }

  /**
   * Fetches the current cart state.
   * @returns {Promise<CartState>}
   */
  async #getCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`);
    return res.json();
  }

  /**
   * Returns every warranty line item currently in the cart.
   * @param {CartLineItem[]} items
   * @returns {CartLineItem[]}
   */
  #findAllWarrantyItems(items) {
    return items.filter((item) => item.product_id === this.#warrantyProductId);
  }

  /**
   * Removes one or more cart line items by key in a single request.
   * Using keys (instead of line indexes) avoids index drift when
   * multiple lines are removed at once.
   * @param {string[]} keys
   * @param {AbortSignal} [signal]
   */
  async #removeLinesByKey(keys, signal) {
    if (keys.length === 0) return;
    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
      signal,
    });
  }

  /**
   * Guarantees at most ONE warranty line in the cart. If more are found
   * (e.g. because of a race between auto-add and a quick manual click,
   * stale state from another tab, or a legacy bug), all extras are
   * removed and only the first is kept.
   */
  async #consolidateWarranties() {
    try {
      const cart = await this.#getCart();
      const warranties = this.#findAllWarrantyItems(cart.items);
      if (warranties.length <= 1) return;

      const extraKeys = warranties.slice(1).map((item) => item.key);
      await this.#removeLinesByKey(extraKeys);
      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    }
  }

  /**
   * If the cart contains only warranty items (no real products),
   * silently remove every warranty line to prevent orphans.
   */
  async #cleanOrphanedWarranty() {
    try {
      const cart = await this.#getCart();
      const hasNonWarranty = cart.items.some((item) => item.product_id !== this.#warrantyProductId);
      if (hasNonWarranty) return;

      const warrantyKeys = this.#findAllWarrantyItems(cart.items).map((item) => item.key);
      if (warrantyKeys.length === 0) return;

      await this.#removeLinesByKey(warrantyKeys);
      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    }
  }

  /**
   * Auto-adds the default (cheapest) warranty variant when:
   *   - a default variant is configured in the snippet,
   *   - the customer hasn't dismissed warranty in this session,
   *   - there's at least one non-warranty product in the cart,
   *   - and no warranty is already present.
   * A global in-flight lock prevents duplicates when multiple
   * cart-warranty instances mount simultaneously (drawer + cart page).
   */
  async #autoAddDefaultWarranty() {
    const variantId = this.#defaultVariantId;
    if (!variantId) return;
    if (sessionStorage.getItem(DISMISSED_STORAGE_KEY) === '1') return;
    if (autoAddInFlight) return;

    autoAddInFlight = true;
    try {
      const cart = await this.#getCart();
      if (this.#findAllWarrantyItems(cart.items).length > 0) return;

      const hasNonWarranty = cart.items.some((item) => item.product_id !== this.#warrantyProductId);
      if (!hasNonWarranty) return;

      await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
          }),
        })
      );

      this.#rerenderAllSections();
    } catch {
      // Fail silently — cart will still work
    } finally {
      autoAddInFlight = false;
    }
  }

  /**
   * Handles warranty variant selection via radio buttons.
   * Removes any existing warranty before adding the newly selected one.
   * @param {Event} event
   */
  selectWarranty = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const variantId = Number(input.value);
    const abortController = this.#createAbortController();

    sessionStorage.removeItem(DISMISSED_STORAGE_KEY);

    try {
      const cart = await this.#getCart();
      const existing = this.#findAllWarrantyItems(cart.items);
      const [first] = existing;

      if (existing.length === 1 && first?.variant_id === variantId) return;

      if (existing.length > 0) {
        await this.#removeLinesByKey(
          existing.map((item) => item.key),
          abortController.signal
        );
      }

      await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
          }),
        }),
        signal: abortController.signal,
      });

      this.#rerenderAllSections();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Removes the active warranty from the cart.
   * @param {Event} event
   */
  removeWarranty = async (event) => {
    event.preventDefault();
    const abortController = this.#createAbortController();

    sessionStorage.setItem(DISMISSED_STORAGE_KEY, '1');

    try {
      const cart = await this.#getCart();
      const keys = this.#findAllWarrantyItems(cart.items).map((item) => item.key);
      if (keys.length === 0) return;

      await this.#removeLinesByKey(keys, abortController.signal);

      this.#rerenderAllSections();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Re-renders every cart-items-component section on the page
   * (covers both cart page and cart drawer).
   */
  #rerenderAllSections() {
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionRenderer.renderSection(el.dataset.sectionId, { cache: false });
      }
    });
  }
}

if (!customElements.get('cart-warranty-component')) {
  customElements.define('cart-warranty-component', CartWarranty);
}
